/**
 * ChatLayout - Layout estrutural do chat
 * Responsabilidade: Container principal com header, sidebar, main e details
 * Princípios: Single Responsibility, Composition
 *
 * Painel de contato em coluna só a partir de 2xl (≥1536px): em xl a soma
 * lista(320)+painel(340)+app nav estoura e o overflow-hidden corta a direita.
 */
import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { MOBILE_ONLY_CONTENT_BOTTOM_PAD_CLASS } from '@/components/layout/layoutNavTokens';

interface ChatLayoutProps {
    /** Conteúdo da sidebar (lista de conversas) */
    sidebar: React.ReactNode;
    /** Conteúdo principal (janela de chat) */
    main: React.ReactNode;
    /** Conteúdo do painel de detalhes (dados do contato) - opcional */
    details?: React.ReactNode;
    /** Mensagem de erro flutuante - opcional */
    error?: React.ReactNode;
    /** Indica se há uma conversa ativa (controle mobile) */
    hasActiveSelection?: boolean;
}

/**
 * ChatLayout - Container principal do chat
 */
export const ChatLayout: React.FC<ChatLayoutProps> = ({
    sidebar,
    main,
    details,
    error,
    hasActiveSelection = false
}) => {
    const layoutRef = useRef<HTMLDivElement>(null);
    const hasDetails = Boolean(details);

    useEffect(() => {
        const viewport = window.visualViewport;
        const element = layoutRef.current;
        if (!hasActiveSelection || !viewport || !element) return;
        const updateViewport = () => {
            const mobile = window.innerWidth < 768;
            element.style.height = mobile ? `${viewport.height}px` : '';
            element.style.top = mobile ? `${viewport.offsetTop}px` : '';
        };
        updateViewport();
        viewport.addEventListener('resize', updateViewport);
        viewport.addEventListener('scroll', updateViewport);
        window.addEventListener('resize', updateViewport);
        return () => {
            viewport.removeEventListener('resize', updateViewport);
            viewport.removeEventListener('scroll', updateViewport);
            window.removeEventListener('resize', updateViewport);
            element.style.height = '';
            element.style.top = '';
        };
    }, [hasActiveSelection]);

    return (
        <div
            ref={layoutRef}
            className={cn(
                'chat-page flex h-full min-h-0 w-full flex-col overflow-hidden bg-background font-sans',
                // Lista: reserva espaço da bottom nav. Conversa: viewport visual (composer sempre visível).
                hasActiveSelection
                    ? 'max-md:fixed max-md:inset-0 max-md:z-30 max-md:bg-chat-window-bg'
                    : MOBILE_ONLY_CONTENT_BOTTOM_PAD_CLASS,
            )}
        >
            <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
                {error && (
                    <div className="absolute left-1/2 top-4 z-50 w-[min(24rem,calc(100%-2rem))] -translate-x-1/2">
                        {error}
                    </div>
                )}

                <aside
                    className={cn(
                        'h-full min-h-0 w-full shrink-0 flex-col border-r border-subtle bg-card md:w-[320px]',
                        hasActiveSelection ? 'hidden md:flex' : 'flex',
                        // Overlay do contato (<2xl): não deixa a lista “vazar” ao lado do painel.
                        hasDetails && 'max-2xl:hidden',
                    )}
                >
                    {sidebar}
                </aside>

                <main
                    className={cn(
                        'relative min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-chat-window-bg',
                        !hasActiveSelection ? 'hidden md:flex' : 'flex w-full',
                        hasDetails && 'max-2xl:invisible max-2xl:pointer-events-none',
                    )}
                >
                    {main}
                </main>

                {hasDetails && (
                    <aside
                        className={cn(
                            // Mobile-first / tablet / xl: tela cheia (evita clip da coluna 340px)
                            'fixed inset-0 z-modal flex min-w-0 w-full max-w-full flex-col overflow-hidden bg-card',
                            'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
                            // 2xl+: coluna lateral com shrink se o shell apertar
                            '2xl:static 2xl:z-auto 2xl:h-full 2xl:w-[340px] 2xl:max-w-[340px] 2xl:min-w-0 2xl:shrink 2xl:basis-[340px] 2xl:pt-0 2xl:pb-0',
                        )}
                        aria-label="Dados do contato"
                    >
                        {details}
                    </aside>
                )}
            </div>
        </div>
    );
};

export default ChatLayout;
